import { ApiOptions, CspError, StateAssert } from '@csp/csp-common-model';
import { toRestOptions } from '@csp/csp-fe-auth';
import { ConsentRestServiceV1 } from '@csp/dmdp-api-client';
import {
  ConsentArtifactCriteriaFieldV1,
  ConsentArtifactQueryTypeV1,
  ConsentArtifactQueryV1,
  ConsentArtifactV1,
} from '@csp/dmdp-api-consent-dto';
import { ConsentArtifact, ConsentArtifactConfigV2 } from '../model/ConsentArtifact';
import { CmsConsentDocument } from '../model/CmsConsentDocument';

const getArtifactsByArtifactCodes = async (
  activeArtifactCodes: string[],
  apiOptions?: ApiOptions,
): Promise<ConsentArtifact[]> => {
  const { artifacts } = await ConsentRestServiceV1.queryArtifacts(
    toRestOptions(apiOptions),
    getArtifactCodeQuery(activeArtifactCodes),
  );

  return artifacts.filter(artifact => artifact.meta?.CONFIG_V2).map(artifact => toConsentArtifact(artifact));
};

const getArtifactCodeQuery = (artifactCodes: string[]): ConsentArtifactQueryV1 => ({
  criterion: {
    fieldCriterion: {
      field: ConsentArtifactCriteriaFieldV1.ARTIFACT_CODE,
      type: ConsentArtifactQueryTypeV1.IN,
      values: artifactCodes,
    },
  },
});

const getArtifactCmsDocument = (cmsConsentType: string, documents: CmsConsentDocument[]): CmsConsentDocument => {
  const document = documents.find(document => document.consentType === cmsConsentType);

  if (!document) {
    throw CspError.badState(`Missing Consent Document content for ${cmsConsentType}`);
  }

  return document;
};

const toConsentArtifact = ({ artifactId, artifactCode, documents, meta }: ConsentArtifactV1): ConsentArtifact => {
  const documentReference = documents[0]?.reference;
  StateAssert.isDefined(documentReference, 'Unexpected undefined documentReference');

  return {
    artifactId,
    artifactCode,
    documentReference,
    config: toConfig(meta as Record<string, ConsentArtifactConfigV2>),
  };
};

const toConfig = (meta: Record<string, ConsentArtifactConfigV2>): ConsentArtifactConfigV2 => {
  const clientType = meta.CONFIG_V2?.clientType;
  const country = meta.CONFIG_V2?.country;
  const version = meta.CONFIG_V2?.version;
  const cmsConsentType = meta.CONFIG_V2?.cmsConsentType;
  StateAssert.isDefined(clientType, 'Unexpected undefined clientType');
  StateAssert.isDefined(country, 'Unexpected undefined country');
  StateAssert.isDefined(version, 'Unexpected undefined version');
  StateAssert.isDefined(cmsConsentType, 'Unexpected undefined cmsConsentType');

  return {
    clientType,
    country,
    version,
    cmsConsentType,
  };
};

export const ConsentArtifactService = {
  getArtifactsByArtifactCodes,
  getArtifactCmsDocument,
  toConfig,
};
